/**
 * **布局状态的宿主侧封装** —— 把 `src/layout/areas.ts` 的纯函数
 * 接进 Vue，并给旧代码留一层**兼容视图**。
 *
 * ## 为什么要有「兼容视图」
 *
 * 重构前，「哪些窗口开着」是宿主里的一个 ref：
 *
 * ```ts
 * const windows = ref({
 *   minimap: true, problems: false, keys: true, theme: false,
 * })
 * ```
 *
 * 菜单栏、左侧工具栏、输出窗口标题栏三处都在**写**它，宿主里还有
 * 两处手写镜像（一处是面板可见性、一处是 `logCollapsed`）。
 *
 * 换成布局树之后，这份状态的真实归属是：
 *
 * | 旧布尔     | 新归属                                        |
 * |---|---|
 * | `minimap`  | 节点图 Area 的 `minimap` 显示项                |

 *  * | `keys`     | 节点图 Area 的 `statusbar` Region              |
 * | `problems` | **是否存在一个「输出」Area**（不是布尔！）      |
 *
 * ⚠️ `problems` 这一条是这次重构里最容易做错的地方：
 * 输出窗口以前是「画布下面的一个兄弟 div」，所以它可以是一个布尔。
 * 在新模型里它是一个**独立的 Area** —— 有位置、可以被切分、可以被最大化。
 * 用布尔存它就等于把它按死回「贴在画布底部的一条」，分屏能力全丢。
 *
 * ## 兼容视图的边界
 *
 * `windows` 是 **computed（只读快照）**，不是 ref。
 * 这是刻意的：写入口收窄成 `setWindow()` / `toggleWindow()` 两个函数，
 * 于是「谁改了状态」在代码里是可搜索的。原来那种「随便哪个组件拿到
 * `windows` 就能 `windows.value.minimap = false`」正是漂移的来源。
 */
import { computed, ref } from 'vue'
import type { ComputedRef, Ref } from 'vue'
import {
  areaIdsOf,
  areasOf,
  closeArea,
  createLayout,
  deserializeLayout,
  editorOf,
  findArea,
  findAreaByEditor,
  firstArea,
  focusArea,
  maximizeArea,
  revealEditor,
  serializeLayout,
  setEditor,
  setRatio,
  setRegion,
  splitArea,
  setPanel,
  toggleRegion,
} from '@/layout/areas'
import type {
  EditorType,
  LayoutDoc,
  LayoutState,
  RegionKey,
  SplitDir,
} from '@/layout/areas'
import {
  buildWorkspaceList,
  loadSavedWorkspaces,
  matchWorkspaceId,
  nextWorkspaceId,
  persistSavedWorkspaces,
} from '@/layout/workspaces'
import type { SavedWorkspace, WorkspaceEntry } from '@/layout/workspaces'
import { LAYOUT_PRESETS } from '@/layout/presets'
import type { LayoutPreset } from '@/layout/presets'

/**
 * 旧代码眼里的「窗口开关」。
 *
 * ⚠️ `tune` 已经删掉 —— 「实时调参」面板连同它所在的 sidebar Region
 * 一起删除了（用户实测：「去掉右侧浮窗和调参窗口，没啥用」）。
 * 那个面板本来只是把设计令牌调好再固化进 `theme.css` 的临时脚手架，
 * 令牌已经定下来了，它就没有存在理由。
 */
export type WindowKey = 'minimap' | 'problems' | 'keys' | 'theme'

export interface WindowsView {
  minimap: boolean
  problems: boolean
  /** 全局底部提示条开着吗（**不是** Region，全应用只有一条） */
  keys: boolean
  /** 主题编辑器窗口开着吗（它是**一个 Area**，不是浮层 —— 见下面的 setWindow） */
  theme: boolean
}

/**
 * 全局底部提示条的显隐。
 *
 * ⚠️ 它**不住在布局树里**，因为它不属于任何一个 Area ——
 * Blender 的提示条也是全应用一根。存成一个独立偏好（和主题一样落盘），
 * 这样分屏/换工作区都不会把它弄丢。
 */
const KEYS_KEY = 'node-print.statusbar.v1'

export const statusBarVisible = ref(readFlag(KEYS_KEY, true))

function readFlag(key: string, fallback: boolean): boolean {
  try {
    const raw = typeof localStorage === 'undefined' ? null : localStorage.getItem(key)
    return raw === null ? fallback : raw === '1'
  } catch {
    return fallback
  }
}

function writeFlag(key: string, value: boolean): void {
  try {
    localStorage?.setItem(key, value ? '1' : '0')
  } catch {
    /* 隐私模式 —— 记不住不该报错 */
  }
}

/**
 * 旧布尔 → 新归属的映射。
 *
 * `problems` 是 `null`（它不是一个 Region，而是一个 Area 的存在性）——
 * 显式写出来是为了让 `setWindow` 里的分支有据可查，而不是靠 `if (key === ...)`。
 */
/*
 * ⚠️ 只有**翻 Region 开关**的那几个在这里。
 * `problems` 和 `theme` 的开关语义是「增删一个 Area」，不走 Region ——
 * 把它们塞进这张表会得到一个不存在的 Region 键。
 */
/*
 * ⚠️ 只剩 `minimap` 了。
 *
 * `keys`（快捷键提示条）原来映射到 `statusbar` Region —— 每个窗口自己一条。
 * 现在它是**全局唯一**的那条底部提示条（Blender 也是这样），
 * 所以不再是一个 Region，而是一个全局显隐偏好（见下面的 `keysVisible`）。
 *
 * `problems` / `theme` 从来没进过这张表：它们的语义是「增删一个 Area」。
 */
const REGION_OF: Record<'minimap', RegionKey> = {
  minimap: 'minimap',
}

/** 窗口开关在 UI 上的名字 —— 菜单和工具栏共用，不各写一份 */
export const WINDOW_LABELS: Record<WindowKey, string> = {
  minimap: '小地图',
  problems: '输出窗口',
  keys: '快捷键条',
  theme: '主题编辑器',
}

export interface UseAreasOptions {
  /**
   * 初始布局树。
   *
   * ⚠️ 不传就用**默认那一份预设**（`documents-graph`：上面文档库、
   * 下面画布）。用户 2027 要的是「文档窗口做成默认窗口」——
   * 打开编辑器先看到自己有哪些文档。
   * 不给默认的话是单个节点图 Area（第一版的行为）。
   */
  initial?: LayoutState
}

/**
 * 默认布局 = 预设里那一份「文档 + 节点图」。
 *
 * ⚠️ 从预设表里按 id 取，不在这里手写一份树：手写的话，
 * 「默认布局」和「工作区菜单里那一项」会变成两棵**看起来一样但不是一个**
 * 的树 —— 于是默认进来的用户在菜单里找不到自己所在的这一份
 * （`matchWorkspaceId` 按结构认，结构一样就认得出来）。
 */
function defaultLayout(): LayoutState {
  const preset = LAYOUT_PRESETS.find((item) => item.id === DEFAULT_LAYOUT_PRESET_ID)
  return preset ? preset.build() : createLayout('graph')
}

/**
 * 默认布局是哪一份预设（用户在「视图 → 工作区」里能点到同一个名字）。
 *
 * ⚠️ 2027 改过两次：先是"只做一个窗口不自动开"，再是"文档窗口做成默认窗口"，
 * 用户看到实物之后定了最终形态 —— **只显示文档窗口**：
 *
 * > 默认只显示文档窗口就行了。
 *
 * 于是"打开一份文档去哪儿看"由**动作**回答（见 `useDocuments.onOpened`
 * 与宿主那边的"没有画布窗口就把当前这个换成节点图"），而不是靠默认摆两个窗口。
 */
export const DEFAULT_LAYOUT_PRESET_ID = 'documents'

export function useAreas(options: UseAreasOptions = {}) {
  const layout = ref<LayoutState>(options.initial ?? defaultLayout())

  /**
   * 用户自存的工作区（内存里的那一份，落盘在 `workspaces.ts`）。
   *
   * ⚠️ `storage` 在 SSR / 隐私模式下可能是 `undefined` —— 两处读写的
   * 工具函数都接受 `null` 并安静退化，所以这里不用分支。
   */
  const storage = typeof localStorage === 'undefined' ? null : localStorage
  const savedWorkspaces = ref<SavedWorkspace[]>(loadSavedWorkspaces(storage))

  /**
   * 标签栏用的完整清单：**自存的在前**，然后是内置预设。
   *
   * 顺序同时决定了匹配优先级 —— 用户存了一份和内置结构相同的布局时，
   * 打勾的是他自己那个名字（那是他刚做的事）。
   */
  const workspaces = computed<WorkspaceEntry[]>(() =>
    buildWorkspaceList(savedWorkspaces.value),
  )

  /**
   * 当前处在哪个工作区（`null` = 结构不对应任何一个 → 显示「自定义」）。
   *
   * ⚠️ **派生**，不是一个需要手动维护的 ref。
   * 第一版写成 `ref` 由 `applyPreset` 赋值，于是用户手动分屏之后
   * 标签上的高亮还留在原来那一项 —— 「我刚才选的是哪个」变得不可信。
   * 派生之后不可能不同步（判断的是**结构**，拖动分隔条不会让它丢）。
   */
  const activeWorkspaceId = computed(() => matchWorkspaceId(layout.value, workspaces.value))

  /*
   * ⚠️ 这里原来有 `oneAtATime` + `settle()`（手机档「一次只显示一个 Area」）。
   *
   * 用户实测后要求**反过来**：「给移动端增加窗口的多窗口功能」——
   * 手机上也允许分屏（他明确说上下结构可用）。所以「开一个窗口就整屏」
   * 这条规则删掉了；想整屏时用 Area header 上的**最大化**按钮，
   * 那是用户主动的决定，不是壳强加的。
   *
   * 顺带说明：`windowToggles` / 模式栏那套「切编辑器」的入口也没了，
   * 改成**预设布局**（见 `src/layout/presets.ts`）——
   * 直接给一棵排好的树，而不是「切过去再自动整屏」。
   */

  /**
   * 「节点图」Area —— 旧的那些窗口开关都挂在它身上。
   *
   * 找不到节点图时（用户把唯一的 Area 换成了别的编辑器）回落到第一个 Area：
   * 这样 `windows` 读出来是全 false，开关点了也没用，但**不会崩**，
   * 也不会出现「开关亮着但界面上什么都没有」。
   */
  const graphAreaId = computed(
    () =>
      (findAreaByEditor(layout.value.root, 'graph') ?? firstArea(layout.value.root)).id,
  )

  /** 输出 Area 的 id（没有就是 null） */
  const consoleAreaId = computed(
    () => findAreaByEditor(layout.value.root, 'console')?.id ?? null,
  )

  /** 主题编辑器 Area 的 id（没有就是 null） */
  const themeAreaId = computed(
    () => findAreaByEditor(layout.value.root, 'theme')?.id ?? null,
  )

  /** 只读快照 —— 旧代码读它不用改一个字 */
  const windows: ComputedRef<WindowsView> = computed(() => {
    const graph = findArea(layout.value.root, graphAreaId.value)
    const regions = graph?.regions
    return {
      minimap: regions?.minimap ?? false,
      keys: statusBarVisible.value,
      problems: consoleAreaId.value !== null,
      theme: themeAreaId.value !== null,
    }
  })

  /* ── 写入口（只有这两个）────────────────────────────────────── */

  /**
   * 新窗口贴哪一侧。
   *
   * ⚠️ 桌面 **左右**（宽屏上编辑器/属性这类内容要宽度），
   * 手机 **上下**（390px 切左右之后每栏不到 200px，节点图没法看）。
   *
   * 判据用 `matchMedia` 而不是 `touchDevice` —— `touchDevice` 是
   * `(hover: none) and (pointer: coarse)`，它问的是「有没有悬停」，
   * 而这里问的是「**屏幕有多宽**」。触屏笔记本有悬停但不该按手机处理，
   * 而一个窄窗口的有鼠标设备反而该上下分。
   */
  function newWindowDir(): 'row' | 'col' {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return 'row'
    /*
     * 720px 和工具栏收起列的阈值一致：它们回答的是同一个问题
     * 「这一屏够不够并排放两个窗口」。两处用同一个数，改的时候一起改。
     */
    return window.matchMedia('(max-width: 720px)').matches ? 'col' : 'row'
  }

  function setWindow(key: WindowKey, value: boolean): void {
    /*
     * ⚠️ 「主题编辑器」和「输出窗口」是**同一类**开关：增删一个 Area，
     * 不是翻一个布尔。两条路径都用 `revealEditor` / `focusArea` ——
     * 连点两次不会切出两个主题窗口。
     */
    if (key === 'theme') {
      if (value) {
        layout.value =
          themeAreaId.value === null
            ? revealEditor(layout.value, 'theme', newWindowDir())
            : focusArea(layout.value, themeAreaId.value)
      } else if (themeAreaId.value !== null) {
        layout.value = closeArea(layout.value, themeAreaId.value)
      }
      return
    }
    if (key === 'problems') {
      /*
       * ⚠️ 「输出窗口」的开关 = **增删一个 Area**，不是翻一个布尔。
       * 打开时用 `revealEditor`（已经有就跳过去，没有才切一个）——
       * 这样连点两次不会切出两个输出 Area。
       */
      if (value) {
        /*
         * ⚠️ 两条路径都要过 `settle`：新建（revealEditor）和
         * 「已经有了，跳过去」（focusArea）。手机档下两者都得整屏 ——
         * 只修一条会出现「第一次开是分屏、再开一次才整屏」的诡异行为。
         */
        layout.value =
          consoleAreaId.value === null
            ? revealEditor(layout.value, 'console', newWindowDir())
            : focusArea(layout.value, consoleAreaId.value)
      } else if (consoleAreaId.value !== null) {
        layout.value = closeArea(layout.value, consoleAreaId.value)
      }
      return
    }
    if (key === 'keys') {
      statusBarVisible.value = value
      writeFlag(KEYS_KEY, value)
      return
    }
    layout.value = setRegion(layout.value, graphAreaId.value, REGION_OF[key], value)
  }

  function toggleWindow(key: WindowKey): void {
    setWindow(key, !windows.value[key])
  }

  /* ── 结构操作（渲染层直接用这些）────────────────────────────── */

  const actions = {
    split: (areaId: string, dir: SplitDir, ratio?: number) => {
      layout.value = splitArea(layout.value, areaId, dir, ratio)
    },
    close: (areaId: string) => {
      layout.value = closeArea(layout.value, areaId)
    },
    setEditor: (areaId: string, editor: EditorType) => {
      layout.value = setEditor(layout.value, areaId, editor)
    },
    setRatio: (splitId: string, ratio: number) => {
      layout.value = setRatio(layout.value, splitId, ratio)
    },
    setRegion: (areaId: string, key: RegionKey, value: boolean) => {
      layout.value = setRegion(layout.value, areaId, key, value)
    },
    toggleRegion: (areaId: string, key: RegionKey) => {
      layout.value = toggleRegion(layout.value, areaId, key)
    },
    /**
     * 设某个 Area 某一侧的侧栏（`null` = 收起）。
     *
     * ⚠️ 面板开合**归布局**（`AreaLeaf.panels`）：header 上的按钮
     * （组件自己 emit）和「⋯」菜单里的开关（宿主生成）读写同一份，
     * 所以两边都必须走这条写入口 —— 各存一份的话，
     * "菜单里开着、header 上却显示关着"这种错会稳定复现。
     */
    setPanelOfSide: (areaId: string, side: 'left' | 'right', tab: string | null) => {
      layout.value = setPanel(layout.value, areaId, side, tab)
    },
    focus: (areaId: string) => {
      layout.value = focusArea(layout.value, areaId)
    },
    maximize: (areaId: string | null) => {
      layout.value = maximizeArea(layout.value, areaId)
    },
    /** 跳到（或切出）某个编辑器 —— 桌面前端手机端是同一条路径 */
    reveal: (editor: EditorType) => {
      layout.value = revealEditor(layout.value, editor)
    },
    /** 套用一份工作区布局（内置预设或用户自存的，见 `layout/workspaces.ts`） */
    applyWorkspace: (entry: WorkspaceEntry) => {
      layout.value = entry.build()
    },
    /**
     * 把**当前**这棵树存成一个命名工作区。
     *
     * 返回新条目的 id（调用方通常用它去定位/高亮）。
     * `label` 为空时给一个「工作区 N」的兜底名字 —— 存不下来比名字丑更烦。
     */
    saveWorkspace: (label: string): string => {
      const id = nextWorkspaceId(savedWorkspaces.value)
      const trimmed = label.trim() || `工作区 ${savedWorkspaces.value.length + 1}`
      savedWorkspaces.value = [
        ...savedWorkspaces.value,
        { id, label: trimmed, layout: serializeLayout(layout.value) },
      ]
      persistSavedWorkspaces(storage, savedWorkspaces.value)
      return id
    },
    /** 删掉一个自存的工作区（内置的删不掉，调用方负责不给入口） */
    deleteWorkspace: (id: string) => {
      savedWorkspaces.value = savedWorkspaces.value.filter((item) => item.id !== id)
      persistSavedWorkspaces(storage, savedWorkspaces.value)
    },
    /**
     * 整体替换布局（打开模板文件 / 载入用户设置的入口）。
     * 走 `deserializeLayout`，坏数据退回当前布局而不是把界面搞白。
     */
    load: (doc: unknown) => {
      layout.value = deserializeLayout(doc, layout.value)
    },
    /** 载入本地保存的布局（localStorage） */
    loadFromStorage: (storageKey: string) => {
      try {
        const raw = globalThis.localStorage?.getItem(storageKey)
        if (!raw) return
        layout.value = deserializeLayout(JSON.parse(raw), layout.value)
      } catch {
        /* 读不到 / 解析不了就保持现状 —— 布局丢了只是难看，不该影响开工 */
      }
    },
    saveToStorage: (storageKey: string) => {
      try {
        globalThis.localStorage?.setItem(
          storageKey,
          JSON.stringify(serializeLayout(layout.value)),
        )
      } catch {
        /* 隐私模式 / 配额满 —— 静默失败，布局不是关键数据 */
      }
    },
  }

  /* ── 给渲染层读的派生量 ─────────────────────────────────────── */

  const areaIds = computed(() => areaIdsOf(layout.value.root))

  /** 当前有焦点的 Area 在显示哪个编辑器（模式栏高亮它） */
  const activeEditor = computed<EditorType>(() => {
    const area = findArea(layout.value.root, layout.value.focused)
    return area ? editorOf(area) : 'graph'
  })

  /**
   * 已经存在 Area 的编辑器清单（**显示的那个**，不是"要显示的那个"）。
   * 模式栏用它区分「切过去就行」和「会新建一个」—— 后者带个小圆点。
   */
  const presentEditors = computed<EditorType[]>(() => [
    ...new Set(areasOf(layout.value.root).map((a) => editorOf(a))),
  ])
  const maximizedId = computed(() => layout.value.maximized)
  const focusedId = computed(() => layout.value.focused)

  return {
    layout,
    windows,
    graphAreaId,
    consoleAreaId,
    workspaces,
    activeWorkspaceId,
    areaIds,
    activeEditor,
    presentEditors,
    maximizedId,
    focusedId,
    setWindow,
    toggleWindow,
    ...actions,
  }
}

export type { LayoutDoc, LayoutState }
